// Commission-worksheet vocabulary, keyed by company.
//
// WHY THIS EXISTS
// ---------------
// The commission worksheet, its CSV export, and the commission PDFs were
// all written against Baker's Mortgage disbursement document and hardcoded
// its nouns: "Total Broker Check", "Loan Originator Split", "Brokerage
// Retained", plus a fixed block of mortgage cost rows (Credit Report,
// Appraisal, Reinspection, Tolerance). Those surfaces are company-agnostic
// — the same worksheet renders Insurance's monthly commission pool — so an
// Insurance disbursement was being described entirely in mortgage terms.
// Brian Baker reported it 2026-07-31: "This is the Insurance Commission
// page. Its Insurance, but the breakdown is Mortgage."
//
// This module is the single source of truth all three surfaces read from,
// so they can't drift apart again.
//
// DECISION: Mortgage's wording is preserved EXACTLY, because it mirrors the
// paper disbursement document Baker reconciles against — changing it would
// break his line-by-line comparison. Every other company gets accurate,
// company-neutral wording ("Advisor Split", "Stewardship Retained") rather
// than invented per-industry nouns. Where a company's own system names the
// number, we use that name: Insurance and Financial both compute gross from
// the QuickBooks "Commission Income" parent account (see
// run-monthly-insurance-disbursement.ts / run-monthly-financial-disbursement.ts),
// so their gross row says "Total Commission Income".
//
// If Baker or a company's comp owner wants industry-specific nouns (e.g.
// "Agency Retained" for Insurance), that's one entry in COMPANY_VOCABULARY
// — not a code change in three files.

export interface DisbursementVocabulary {
  /** Top line — the money that arrived before any deduction. */
  gross_label: string;
  /** Italic heading over the cost rows. */
  costs_heading: string;
  /** Gross minus costs. */
  net_label: string;
  /** The advisor's share, expressed as a rate. */
  split_label: string;
  /** The advisor's share, expressed as a dollar total. */
  disbursement_label: string;
  /** What Stewardship keeps. */
  retained_label: string;
  /**
   * Totals row on the monthly Commission Report PDF.
   *
   * Mortgage's parenthetical says "loans this month" because a mortgage
   * report IS a list of loans. Insurance's is a single monthly
   * commission pool with no loans in it, and printing "loans" there
   * described the wrong business on Brandon's own report (Baker,
   * 2026-08-10: "loans doesnt need to be on the PDF that he gets
   * emailed since it is Insurance Commissions not Mortgage").
   */
  report_total_disbursement_label: string;
  /**
   * Cost types rendered as fixed rows even when absent (shown as "$-").
   *
   * Mortgage lists all six because Baker's paper document has all six
   * lines whether or not they were charged; a blank line is meaningful
   * there. Every other company gets an empty list — the whole
   * commission_transaction_costs cost_type enum is mortgage-specific
   * (credit_report / appraisal / reinspection / tolerance / flood_cert /
   * title / escrow), so listing those rows on an Insurance worksheet
   * asserts deductions that cannot exist. Non-mortgage worksheets render
   * only cost rows that actually have data.
   */
  standard_cost_types: readonly string[];
  /** How this company names the person receiving the disbursement. */
  advisor_role_label: string;
  /** First-line title of the CSV export. */
  csv_title: string;
  /** Column header for the "what generated this" column in report PDFs. */
  record_column_label: string;
  /** Column header for the counterparty column in report PDFs. */
  counterparty_column_label: string;
  /**
   * Whether the Commission Worksheet's reconciliation card shows the
   * "CD gross matches" row. "CD" is the Closing Disclosure — a
   * mortgage closing document — and the row asks Baker to check the
   * disbursement against it. That reconciliation step doesn't exist
   * for any other company, so the row described a mortgage-only
   * process on every worksheet regardless of company (Baker,
   * 2026-08-11: "We dont need the CD language ... since it is
   * Insurance"). The "QB deposit matches" row + audit timestamps below
   * it stay for every company — only the CD-specific row is gated.
   */
  show_cd_reconciliation: boolean;
}

/**
 * Mortgage's set. Also the shape every other entry overrides from — kept
 * separate (rather than spread into a base) so a reader can see Baker's
 * document verbatim in one place.
 */
const MORTGAGE_VOCABULARY: DisbursementVocabulary = {
  gross_label: "Total Broker Check",
  costs_heading: "Minus Stewardship Transaction Costs",
  net_label: "NET Income",
  split_label: "Loan Originator Split",
  disbursement_label: "Total Loan Originator Disbursement",
  retained_label: "Brokerage Retained",
  report_total_disbursement_label: "Total disbursement (loans this month)",
  standard_cost_types: [
    "credit_report",
    "processing",
    "appraisal",
    "reinspection",
    "tolerance",
    "other",
  ],
  advisor_role_label: "Loan Originator",
  csv_title: "Disbursement of funds on loan file closing",
  record_column_label: "Loan",
  counterparty_column_label: "Borrower",
  show_cd_reconciliation: true,
};

/**
 * The company-neutral set — true for any company, asserts nothing
 * industry-specific. Used directly for planning / realty / cross-company
 * ("all") rows and as the base for Insurance + Financial.
 */
const NEUTRAL_VOCABULARY: DisbursementVocabulary = {
  gross_label: "Total Commission Received",
  costs_heading: "Minus Stewardship Transaction Costs",
  net_label: "NET Income",
  split_label: "Advisor Split",
  disbursement_label: "Total Advisor Disbursement",
  retained_label: "Stewardship Retained",
  report_total_disbursement_label: "Total disbursement (this month)",
  standard_cost_types: [],
  advisor_role_label: "Advisor",
  csv_title: "Commission disbursement",
  record_column_label: "Item",
  counterparty_column_label: "Detail",
  show_cd_reconciliation: false,
};

const COMPANY_VOCABULARY: Record<string, DisbursementVocabulary> = {
  mortgage: MORTGAGE_VOCABULARY,

  // Gross is the QuickBooks "Commission Income" parent-account total —
  // Brandon's monthly pool. Named after the account Baker reconciles it
  // against.
  insurance: {
    ...NEUTRAL_VOCABULARY,
    gross_label: "Total Commission Income",
    csv_title: "Insurance commission disbursement",
  },

  // Same QB-derived shape as Insurance (see run-monthly-financial-disbursement.ts).
  financial: {
    ...NEUTRAL_VOCABULARY,
    gross_label: "Total Commission Income",
    csv_title: "Financial commission disbursement",
  },

  planning: {
    ...NEUTRAL_VOCABULARY,
    csv_title: "Planning commission disbursement",
  },

  // A real-estate brokerage genuinely retains the office split, so
  // "Brokerage Retained" is accurate here rather than borrowed.
  realty: {
    ...NEUTRAL_VOCABULARY,
    retained_label: "Brokerage Retained",
    csv_title: "Realty commission disbursement",
  },
};

/**
 * Labels for one disbursement's company. Unknown / cross-company ("all")
 * values fall back to the neutral set — never to Mortgage's, because a
 * wrong-but-specific label is what caused this bug.
 */
export function disbursementVocabulary(
  company: string | null | undefined,
): DisbursementVocabulary {
  const key = (company ?? "").trim().toLowerCase();
  return COMPANY_VOCABULARY[key] ?? NEUTRAL_VOCABULARY;
}

const COMPANY_DISPLAY_LABELS: Record<string, string> = {
  mortgage: "Mortgage",
  insurance: "Insurance",
  planning: "Planning",
  realty: "Realty",
  financial: "Financial",
  all: "All companies",
};

/** "insurance" → "Insurance". Falls back to the raw slug. */
export function companyDisplayLabel(company: string | null | undefined): string {
  const key = (company ?? "").trim().toLowerCase();
  return COMPANY_DISPLAY_LABELS[key] ?? (company ?? "—");
}

/**
 * Does this disbursement cover a whole pay period, or a single record?
 *
 * Matters because the paycheck split ("$604.75 on May 15, $604.75 on May
 * 31") is a payroll instruction, and payroll runs per period, not per
 * loan. A per-loan Mortgage worksheet carries a period so it can ROLL UP
 * into that month's payroll — printing its own two-check split would
 * assert a pay cadence for one loan that nobody runs.
 */
export function isPeriodPoolDisbursement(
  entityType: string | null | undefined,
): boolean {
  return (entityType ?? "").trim() === "commission_disbursement_pool";
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  commission_disbursement_pool: "Commission pool",
  mortgage_loan: "Loan",
  // A manager-override payment generated alongside a loan officer's own
  // worksheet (e.g. Brian Ream's $500 when Joey Ellis closes a file) —
  // never the loan officer's own commission, which stays "Loan".
  mortgage_loan_kickback: "Manager override",
  // Realty pay from money placed on a closing (commissions build program
  // C4): the agent, the manager's share or broker review, the Ambassador fee.
  realty_closing: "Realty closing",
  transaction: "Processing record",
  service_record: "Service record",
  opportunity: "Opportunity",
};

/**
 * "commission_disbursement_pool" → "Commission pool". Unknown types are
 * title-cased rather than dropped, so a new entity_type degrades to
 * readable text instead of vanishing from the header.
 */
export function entityTypeLabel(entityType: string | null | undefined): string {
  const key = (entityType ?? "").trim().toLowerCase();
  const known = ENTITY_TYPE_LABELS[key];
  if (known) return known;
  if (!key) return "—";
  return key
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}
